import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, useProfile } from "@/hooks/useAuth";
import { useActiveOrg } from "@/contexts/ActiveOrgContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, Shield, Loader2 } from "lucide-react";
import { HubHeader } from "./hub/HubHeader";
import { OrgGrid } from "./hub/OrgGrid";
import { SuperadminPanel } from "./hub/SuperadminPanel";

export default function Hub() {
  const { user } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const { memberships, setActiveOrgId, loading } = useActiveOrg();

  const { data: userRoles = [] } = useQuery({
    queryKey: ["myRoles", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id);
      return (data ?? []).map(r => r.role);
    },
    enabled: !!user,
  });
  const isSuperAdmin = userRoles.includes("superadmin");

  const handleEnterOrg = (orgId: string) => {
    setActiveOrgId(orgId);
    navigate("/dashboard");
  };

  const initials = profile?.first_name && profile?.last_name
    ? `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase()
    : user?.email?.[0]?.toUpperCase() ?? "U";

  const displayName = profile?.full_name || profile?.first_name || user?.email?.split("@")[0] || "Utilisateur";

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <HubHeader initials={initials} displayName={displayName} email={user?.email} />

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-display">
            Bonjour{user?.user_metadata?.full_name ? `, ${user.user_metadata.full_name}` : ""} 👋
          </h1>
          <p className="text-muted-foreground mt-1">
            Sélectionnez une organisation pour commencer à travailler.
          </p>
        </div>

        {isSuperAdmin ? (
          <Tabs defaultValue="my-orgs">
            <TabsList>
              <TabsTrigger value="my-orgs" className="gap-2">
                <Building2 className="w-4 h-4" /> Mes organisations
              </TabsTrigger>
              <TabsTrigger value="admin" className="gap-2">
                <Shield className="w-4 h-4" /> Administration
              </TabsTrigger>
            </TabsList>
            <TabsContent value="my-orgs" className="mt-6">
              <OrgGrid memberships={memberships} onEnter={handleEnterOrg} />
            </TabsContent>
            <TabsContent value="admin" className="mt-6">
              <SuperadminPanel />
            </TabsContent>
          </Tabs>
        ) : (
          <OrgGrid memberships={memberships} onEnter={handleEnterOrg} />
        )}
      </main>
    </div>
  );
}
